import { create } from "zustand";
import type {
  LibraryTemplate,
  GetLibraryRequestBody,
  CopyFromLibraryRequestBody,
  CopyFromLibraryResponse,
} from "@/common/types/templates";

import {
  getTemplateLibrary,
  getTemplateCategories,
  copyTemplateFromLibrary,
} from "@/services/templateLibrary";
import { getTemplateLanguage } from "@/utils/languageMapping";

type State = {
  libraryTemplates: LibraryTemplate[];
  libraryLoading: boolean;
  libraryError?: string | null;
  // Category-based lazy loading
  categoryTemplates: Record<string, LibraryTemplate[]>;
  categoryLoading: Record<string, boolean>;
  categories: string[];
  categoriesLoading: boolean;

  copyingId?: string | null;
};

type Actions = {
  fetchAndSetLibrary: (body?: GetLibraryRequestBody) => Promise<void>;

  /**
   *
   * @param currentLanguage
   * @param body
   */
  fetchAndSetLibraryByLanguage: (
    currentLanguage: string,
    body?: GetLibraryRequestBody
  ) => Promise<void>;

  /**
   * Fetch categories list only (lightweight)
   */
  fetchCategories: (language?: string) => Promise<string[]>;

  /**
   * Fetch templates for a specific category (lazy loading)
   */
  fetchTemplatesByCategory: (
    category: string,
    language?: string
  ) => Promise<LibraryTemplate[]>;

  /**
   * Clear cached data for a specific language (useful when language changes)
   */
  clearLanguageCache: (language?: string) => void;

  /**
   *
   * @param body  { userId, libraryTemplateId }
   * @param onCopied
   */
  copyToMyTemplates: (
    body: CopyFromLibraryRequestBody,
    onCopied?: (created: CopyFromLibraryResponse) => void
  ) => Promise<CopyFromLibraryResponse | null>;

  getLibraryById: (id: string) => LibraryTemplate | undefined;
};

export const useTemplateLibraryStore = create<State & Actions>((set, get) => ({
  libraryTemplates: [],
  libraryLoading: false,
  libraryError: null,
  categoryTemplates: {},
  categoryLoading: {},
  categories: [],
  categoriesLoading: false,
  copyingId: null,

  fetchAndSetLibrary: async (body): Promise<void> => {
    set({ libraryLoading: true, libraryError: null });
    try {
      const data = await getTemplateLibrary(body ?? {});
      set({
        libraryTemplates: data,
        libraryLoading: false,
        libraryError: null,
      });
    } catch (error: unknown) {
      console.error("Failed to fetch library templates:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to load template library";
      set({
        libraryTemplates: [],
        libraryLoading: false,
        libraryError: errorMessage,
      });
    }
  },

  fetchAndSetLibraryByLanguage: async (
    currentLanguage,
    body
  ): Promise<void> => {
    set({ libraryLoading: true, libraryError: null });
    try {
      const mappedLanguage = getTemplateLanguage(currentLanguage);

      const filterBody = {
        ...body,
        language: mappedLanguage,
      };

      const data = await getTemplateLibrary(filterBody);

      if (data.length === 0 && mappedLanguage !== "en") {
        const fallbackData = await getTemplateLibrary({
          ...body,
          language: "en",
        });
        set({
          libraryTemplates: fallbackData,
          libraryLoading: false,
          libraryError: null,
        });
      } else {
        set({
          libraryTemplates: data,
          libraryLoading: false,
          libraryError: null,
        });
      }
    } catch (error: unknown) {
      console.error("Failed to fetch library templates by language:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to load template library";
      set({
        libraryTemplates: [],
        libraryLoading: false,
        libraryError: errorMessage,
      });
    }
  },

  copyToMyTemplates: async (
    body,
    onCopied
  ): Promise<CopyFromLibraryResponse | null> => {
    try {
      set({ copyingId: body.libraryTemplateId.toString() });
      const created = await copyTemplateFromLibrary(body);
      onCopied?.(created);
      return created;
    } catch (error: unknown) {
      console.error("Failed to copy template from library:", error);
      throw error;
    } finally {
      set({ copyingId: null });
    }
  },

  getLibraryById: (id): LibraryTemplate | undefined =>
    get().libraryTemplates.find((t) => t.id.toString() === id),

  fetchCategories: async (language?: string): Promise<string[]> => {
    set({ categoriesLoading: true });
    try {
      const mappedLanguage = language
        ? getTemplateLanguage(language)
        : undefined;
      // Use categoriesOnly query if supported, otherwise fetch all
      try {
        const categoriesData = await getTemplateCategories({
          language: mappedLanguage,
        });
        const sorted = [...categoriesData].sort((a, b) => {
          if (a === "Uncategorized") return 1;
          if (b === "Uncategorized") return -1;
          return a.localeCompare(b);
        });
        set({ categories: sorted, categoriesLoading: false });
        return sorted;
      } catch {
        // Fallback: extract from templates
      }

      // Fallback: fetch templates and extract categories
      const data = await getTemplateLibrary({
        language: mappedLanguage,
        limit: 10000, // Large limit to get all for category extraction
      });

      // Ensure data is LibraryTemplate[]
      if (!Array.isArray(data) || data.length === 0) {
        set({ categories: [], categoriesLoading: false });
        return [];
      }

      const templates = data;
      const categorySet = new Set<string>();
      for (const template of templates) {
        const category = template.category || "Uncategorized";
        categorySet.add(category);
      }

      const categories = Array.from(categorySet).sort((a, b) => {
        if (a === "Uncategorized") return 1;
        if (b === "Uncategorized") return -1;
        return a.localeCompare(b);
      });

      set({ categories, categoriesLoading: false });
      return categories;
    } catch (error: unknown) {
      console.error("Failed to fetch categories:", error);
      set({ categories: [], categoriesLoading: false });
      return [];
    }
  },

  fetchTemplatesByCategory: async (
    category: string,
    language?: string
  ): Promise<LibraryTemplate[]> => {
    // Map language first to ensure consistent cache key
    const mappedLanguage = language ? getTemplateLanguage(language) : undefined;
    const languageKey = mappedLanguage || "all";
    const categoryKey = `${category}-${languageKey}`;

    // Check if already loaded
    const existing = get().categoryTemplates[categoryKey];
    if (existing) {
      return existing;
    }

    // Check if already loading
    if (get().categoryLoading[categoryKey]) {
      // Wait for existing request
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          const loaded = get().categoryTemplates[categoryKey];
          const loading = get().categoryLoading[categoryKey];
          if (loaded || !loading) {
            clearInterval(checkInterval);
            resolve(loaded || []);
          }
        }, 100);
      });
    }

    set((state) => ({
      categoryLoading: { ...state.categoryLoading, [categoryKey]: true },
    }));

    try {
      const data = await getTemplateLibrary({
        category,
        language: mappedLanguage,
      });

      set((state) => ({
        categoryTemplates: {
          ...state.categoryTemplates,
          [categoryKey]: data,
        },
        categoryLoading: {
          ...state.categoryLoading,
          [categoryKey]: false,
        },
      }));

      return data;
    } catch (error: unknown) {
      console.error(
        `Failed to fetch templates for category ${category}:`,
        error
      );
      set((state) => ({
        categoryLoading: {
          ...state.categoryLoading,
          [categoryKey]: false,
        },
      }));
      return [];
    }
  },

  clearLanguageCache: (language?: string): void => {
    const mappedLanguage = language ? getTemplateLanguage(language) : undefined;
    const languageKey = mappedLanguage || "all";

    set((state) => {
      // Clear category templates for this language
      const newCategoryTemplates: Record<string, LibraryTemplate[]> = {};
      const newCategoryLoading: Record<string, boolean> = {};

      for (const [key, value] of Object.entries(state.categoryTemplates)) {
        if (!key.endsWith(`-${languageKey}`)) {
          newCategoryTemplates[key] = value;
        }
      }

      for (const [key, value] of Object.entries(state.categoryLoading)) {
        if (!key.endsWith(`-${languageKey}`)) {
          newCategoryLoading[key] = value;
        }
      }

      return {
        categoryTemplates: newCategoryTemplates,
        categoryLoading: newCategoryLoading,
        // Clear categories list to force reload
        categories: [],
      };
    });
  },
}));
